import React from 'react';
import PropTypes from 'prop-types';

import AvailableLanguageLink from '../AvailableLanguageLink';
import { Link } from 'src/components/Inputs';

/*
 * When making changes to this file be sure that any changes
 * also are made to the corresponding iOS specific counterpart
 * in src/pages/<lang>/legal/ios.jsx.
 */

const propTypes = {
  id: PropTypes.string,
  availableLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};

const EnVersion = ({ id, availableLanguages }) => {
  return (
    <>
      <h4
        id={id || 'cookie-policy'}
        data-magellan-target={id || 'cookie-policy'}
      >
        Cookie Policy
      </h4>

      <p>Available languages:</p>
      <ul>
        {availableLanguages.map((language, i) => {
          return (
            <AvailableLanguageLink
              key={`language-${i}`}
              href={language.href}
              label={language.label}
            />
          );
        })}
      </ul>
      <br />
      <br />

      <p>Effective Date: August 22, 2023</p>
      <p>
        This Cookie Policy (“Policy”) explains how Plaid Inc. and its
        subsidiaries, including Plaid Financial Ltd., Plaid Consumer Reporting
        Agency, Inc., and Plaid, B.V., (collectively, “we”, “our”, or “us”), and
        trusted third parties, use cookies on our websites and other online
        services (collectively the “Services”), as well as your choices related
        to those cookies. Please review this Policy to learn more about the
        types of cookies we use, why we use them, and how you can control or
        limit the use of cookies.
      </p>
      <p>
        To find out more about our privacy practices, please refer to our{' '}
        <Link href='#end-user-privacy-policy'>
          <a>End User Privacy Policy</a> and our{' '}
        </Link>
        <Link href='#privacy-statement'>
          <a>Privacy Statement</a>.
        </Link>
      </p>

      <p>
        <em>Jump to section:</em>
      </p>
      <p>
        <Link href='#what-are-cookies'>
          <a data-smooth-scroll data-offset='80'>
            What are cookies?
          </a>
        </Link>
      </p>

      <p>
        <Link href='#how-we-use-cookies'>
          <a data-smooth-scroll data-offset='80'>
            How We Use Cookies
          </a>
        </Link>
      </p>

      <p>
        <Link href='#your-choices-cookies'>
          <a data-smooth-scroll data-offset='80'>
            Your Choices
          </a>
        </Link>
      </p>

      <p>
        <Link href='#changes-to-this-cookies-policy'>
          <a data-smooth-scroll data-offset='80'>
            Changes to This Policy
          </a>
        </Link>
      </p>

      <p>
        <Link href='#contacting-us-cookies'>
          <a data-smooth-scroll data-offset='80'>
            Contacting Us
          </a>
        </Link>
      </p>

      <h5 id='what-are-cookies'>What Are Cookies</h5>
      <p>
        Cookies are small data files stored on your browser or device, which can
        be in the form of session cookies (which expire once you close your web
        browser) or persistent cookies (which stay on your browser for a set
        period of time or until you delete them). They generally contain
        information such as website preferences, user settings, and browsing
        history. This information helps us recognize you as you interact with or
        revisit our Services.
      </p>
      <p>
        Cookies may be served by the entity that operates the website you are
        visiting (“first-party cookies”) or by other companies (“third-party
        cookies”). For example, we use first-party cookies to remember your
        settings and preferences, such as language and location. We also
        integrate third-party analytics cookies, like Google Analytics, onto our
        websites to help us understand how you are using our websites so that we
        can improve them.
      </p>
      <p>
        We also use other technologies that function similarly to cookies. For
        instance, we use web beacons (also called pixels), which are small
        images on a web page or in an email. Web beacons collect information
        about your browser or device and can set cookies. In addition, we use
        local storage, which allows data to be stored locally on your browser or
        device.
      </p>

      <h5 id='how-we-use-cookies'>How We Use Cookies</h5>
      <p>
        We use cookies for a number of reasons, like helping us determine the
        popularity of certain content, improving our Services and your
        experience, and to better understand your online activity. The cookies
        we use generally fall into one or more of the following categories:
      </p>
      <div className='eupp-container'>
        <table>
          <thead>
            <tr>
              <th>Category of Cookies</th>
              <th>How We Use These Cookies</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Strictly Necessary</td>
              <td>
                These cookies are strictly necessary for our Services to
                function properly. Some of our Services depend on these cookies
                and may not work properly, or at all, if they are disabled.
              </td>
            </tr>
            <tr>
              <td>Functional</td>
              <td>
                These cookies enable the website to provide enhanced
                functionality and personalisation. They may be set by us or by
                third party providers whose services we have added to our pages.
                If you do not allow these cookies then some or all of these
                services may not function properly.
              </td>
            </tr>
            <tr>
              <td>Performance</td>
              <td>
                We use these cookies to better understand how you interact with
                our Services so that we can improve them. <br />
                <br />
                For example, we may use these cookies to determine if you have
                interacted with certain content or features. We can also use
                these cookies to learn more about which features are the most
                popular with our users and where we may need to make
                improvements.
              </td>
            </tr>
            <tr>
              <td>Targeting / Advertising</td>
              <td>
                We and our trusted advertising partners use these cookies to
                display advertisements, to make advertisements more relevant to
                visitors to our websites, and to track the efficiency of any
                advertising campaigns, both on our Services and on other
                websites.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h5 id='your-choices-cookies'>Your Choices</h5>
      <p>
        You have the right to choose whether or not to accept cookies. However,
        since cookies can be an important part of how our Services work, if you
        remove or reject cookies, this could affect the availability and
        functionality of our Services.
      </p>
      <p>
        Below are some options to control or limit how cookies are used on our
        Services:
      </p>
      <ul>
        <li>
          <p>
            You can usually manage settings to remove or reject browser cookies
            manually within your browser’s configuration settings. To manage
            these settings, please follow the instructions given by your
            browser.
          </p>
        </li>
        <li>
          <p>
            To prevent your data from being used by Google Analytics, you can
            install{' '}
            <Link href='https://tools.google.com/dlpage/gaoptout'>
              <a>Google’s opt-out browser add-on</a>
            </Link>
            .
          </p>
        </li>
        <li>
          <p>
            For information on how our advertising partners may allow you to opt
            out of receiving ads based on your web browsing history, please
            visit{' '}
            <Link href='http://optout.aboutads.info/'>
              <a>http://optout.aboutads.info/</a>
            </Link>
            . In addition, European users should visit{' '}
            <Link href='http://www.youronlinechoices.eu/'>
              <a>European Interactive Digital Advertising Alliance</a>
            </Link>{' '}
            and Canadian users should visit{' '}
            <Link href='https://youradchoices.ca/'>
              <a>Digital Advertising Alliance of Canada</a>
            </Link>{' '}
            to learn more about how to opt out of receiving these types of ads.
          </p>
        </li>
      </ul>

      <h5 id='changes-to-this-cookies-policy'>Changes to This Policy</h5>
      <p>
        We may update or change the cookies (or similar technologies) we use
        from time to time. The most up-to-date version of this Policy will be
        posted on our website at{' '}
        <Link href='https://plaid.com/legal'>
          <a>https://plaid.com/legal</a>
        </Link>
        , and if we make any changes, we will update the effective date at the
        top of the Policy. We recommend that you check back periodically for any
        changes.
      </p>

      <h5 id='contacting-us-cookies'>Contacting Us</h5>
      <p>
        If you have any questions about our use of cookies or this Policy, you
        can contact us at{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>
        .
      </p>

      <hr className='section-divider' />
    </>
  );
};

EnVersion.propTypes = propTypes;
export default EnVersion;
